import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  IonApp,
  setupIonicReact,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Route, Redirect } from "react-router-dom";
import {
  homeOutline,
  footballOutline,
  podiumOutline,
  personOutline,
  timeOutline,
} from "ionicons/icons";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme */
import "./theme/variables.css";
import "./theme/app.css";

/* Pages */
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Landing from "./pages/Landing";
import HowItWorks from "./pages/HowItWorks";
import Matches from "./pages/Matches";
import Predictions from "./pages/Predictions";
import Profile from "./pages/Profile";
import Teams from "./pages/Teams";
import Matchday from "./pages/Matchday";
import Leaderboard from "./pages/Leaderboard";
import AllLeagues from "./pages/AllLeagues";
import History from "./pages/History";
import SeasonStandings from "./pages/SeasonStandings";
import AdminDashboard from "./pages/AdminDashboard";
import { useI18n } from "./i18n";

/* Store */
import { useAuthStore } from "./store/authStore";

setupIonicReact();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

const AppTabs: React.FC = () => {
  const { t } = useI18n();

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path="/home" component={Home} />
        <Route path="/matchday/:seasonId/:matchday" component={Matchday} />
        <Route path="/matches" component={Matches} />
        <Route path="/predictions" component={Predictions} />
        <Route path="/teams" component={Teams} />
        <Route path="/leaderboard" component={Leaderboard} />
        <Route path="/all-leagues" component={AllLeagues} />
        <Route exact path="/history" component={History} />
        <Route path="/history/:seasonId" component={SeasonStandings} />
        <Route path="/profile" component={Profile} />
        <Route path="/admin" component={AdminDashboard} />
        <Route exact path="/how-it-works" component={HowItWorks} />
        <Route exact path="/" render={() => <Redirect to="/home" />} />
      </IonRouterOutlet>

      <IonTabBar slot="bottom">
        <IonTabButton tab="home" href="/home">
          <IonIcon icon={homeOutline} />
          <IonLabel>{t("home")}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="matches" href="/matches">
          <IonIcon icon={footballOutline} />
          <IonLabel>{t("matches")}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="leaderboard" href="/leaderboard">
          <IonIcon icon={podiumOutline} />
          <IonLabel>{t("leaderboard")}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="history" href="/history">
          <IonIcon icon={timeOutline} />
          <IonLabel>{t("historyTitle")}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="profile" href="/profile">
          <IonIcon icon={personOutline} />
          <IonLabel>{t("profile")}</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  return (
    <QueryClientProvider client={queryClient}>
      <IonApp>
        <IonReactRouter>
          {isAuthenticated ? (
            <AppTabs />
          ) : (
            <IonRouterOutlet>
              <Route exact path="/" component={Landing} />
              <Route exact path="/how-it-works" component={HowItWorks} />
              <Route exact path="/login" component={Login} />
              <Route exact path="/register" component={Register} />
            </IonRouterOutlet>
          )}
        </IonReactRouter>
      </IonApp>
    </QueryClientProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(<App />);
